/**
 * SO-02 daily puzzle: one level per local date, identical worldwide. The level comes from
 * GET /v1/daily/:date, or offline from the shared generator spec (`dailySpec`, the same
 * bytes the server builds). One result per day: it is kept locally at once and submitted
 * to /v1/daily/:date/result when the API is reachable (retried after sync).
 */
import { emojiGrid, type LevelV1, type Round } from '@mp/core';
import { ROUTES, errorCode, isOffline, type ApiClient } from './api.js';
import type { DailyResultDto, DailyViewDto, LinkCreatedDto, LinkResultDto, RoundBody } from './dto.js';
import type { PlayerState } from './state.js';
import type { PlayerStore } from './store.js';
import type { SyncService } from './sync.js';
import { localDayKey } from './time.js';

export type DailyStatus =
  | { kind: 'available'; date: string }
  | { kind: 'done'; date: string; result: LinkResultDto; nextAt: number; pending: boolean };

/** Local midnight after `now` (the next puzzle). */
export function nextLocalMidnight(now: number): number {
  const d = new Date(now);
  d.setHours(24, 0, 0, 0);
  return d.getTime();
}

export function dailyStatus(s: PlayerState, now: number): DailyStatus {
  const date = localDayKey(now);
  const p = s.dailyPuzzle;
  if (p && p.date === date) return { kind: 'done', date, result: p.result, nextAt: nextLocalMidnight(now), pending: p.pending !== null };
  return { kind: 'available', date };
}

/** Result card numbers from the finished round (the server recomputes the same from its replay). */
export function localResult(r: Round): LinkResultDto {
  const o = r.outcome!;
  return { moves: o.taps, timeMs: o.durationMs, stars: o.stars, grid: emojiGrid(r.trace, r.cap, o.result === 'win'), result: o.result };
}

/** "3:05" style duration. */
export function mmss(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** "5 h 12 min" countdown parts. */
export function countdown(ms: number): { h: number; m: number; s: number } {
  const t = Math.max(0, Math.floor(ms / 1000));
  return { h: Math.floor(t / 3600), m: Math.floor((t % 3600) / 60), s: t % 60 };
}

export interface DailyLevel {
  date: string;
  level: LevelV1;
  seed: number;
  /** generated on the device (API unreachable) */
  offline: boolean;
  done: LinkResultDto | null;
}

export interface DailyDeps {
  api: ApiClient;
  store: PlayerStore;
  sync: SyncService;
  now: () => number;
  /** offline fallback: generateLevel(dailySpec(date)) + dailySeed(date), loaded lazily */
  generate: (date: string) => Promise<{ level: LevelV1; seed: number }>;
}

export class DailyService {
  constructor(private d: DailyDeps) {}

  today(): string {
    return localDayKey(this.d.now());
  }

  status(): DailyStatus {
    return dailyStatus(this.d.store.state, this.d.now());
  }

  async load(date = this.today()): Promise<DailyLevel> {
    if (this.d.api.enabled) {
      try {
        const v = await this.d.api.call<DailyViewDto>('GET', ROUTES.daily(date));
        if (v.done && this.d.store.state.dailyPuzzle?.date !== date)
          this.d.store.update((s) => (s.dailyPuzzle = { date, roundId: '', result: v.done!, pending: null, linkUrl: null }));
        return { date, level: v.level as LevelV1, seed: v.seed, offline: false, done: v.done };
      } catch (e) {
        if (!isOffline(e)) throw e;
      }
    }
    const g = await this.d.generate(date);
    return { date, ...g, offline: true, done: null };
  }

  /**
   * Keep the result locally now; submit when possible. `linkId`: played from a friend's daily
   * link (SO-02: both players see the results side by side and get the sticker).
   */
  async record(date: string, round: RoundBody, result: LinkResultDto, linkId?: string): Promise<LinkResultDto> {
    const pending = { round, localDate: date, ...(linkId ? { linkId } : {}) };
    this.d.store.update((s) => (s.dailyPuzzle = { date, roundId: round.roundId, result, pending, linkUrl: null }));
    return (await this.submit()) ?? result;
  }

  /** Send a pending result; returns the server's card when accepted. */
  async submit(): Promise<LinkResultDto | null> {
    const p = this.d.store.state.dailyPuzzle;
    if (!p?.pending || !this.d.api.enabled) return null;
    try {
      const r = await this.d.api.call<DailyResultDto>('POST', ROUTES.dailyResult(p.date), p.pending);
      this.d.store.update((s) => {
        if (s.dailyPuzzle?.date === p.date) s.dailyPuzzle = { ...s.dailyPuzzle, result: r.result, pending: null };
      });
      this.d.sync.adopt(r.snapshot as never);
      return r.result;
    } catch (e) {
      if (isOffline(e)) return null;
      // refused for good (already played on another device, too old…): keep the local card
      this.d.store.update((s) => {
        if (s.dailyPuzzle?.date === p.date) s.dailyPuzzle = { ...s.dailyPuzzle, pending: null };
      });
      if (errorCode(e) === 'already_played') return null;
      throw e;
    }
  }

  /** SO-02 share: a daily link for today's result (after it reached the server). */
  async link(): Promise<LinkCreatedDto> {
    const p = this.d.store.state.dailyPuzzle;
    if (!p) throw new Error('no daily result');
    if (p.pending) await this.submit();
    const res = await this.d.api.call<LinkCreatedDto>('POST', ROUTES.dailyLink, { date: p.date, roundId: p.roundId });
    this.d.store.update((s) => {
      if (s.dailyPuzzle?.date === p.date) s.dailyPuzzle = { ...s.dailyPuzzle, linkUrl: res.url };
    });
    return res;
  }
}
